import React, { useState, useEffect } from 'react';
import { Tabs, message } from 'antd';
import styled from 'styled-components';
import PlanConfiguration from './PlanConfiguration';
import SubscriptionRequests from './SubscriptionRequests';
import UsageAlerts from './UsageAlerts';
import UserTokenManagement from './UserTokenManagement';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';

const PageContainer = styled.div`
    width: 95%;
    margin-left: auto;
    padding: 20px 0;
`;

const PageTitle = styled.h1`
    color: #002C57;
    font-size: 24px;
    margin-bottom: 8px;
`;

const PageDescription = styled.p`
    color: #666;
    font-size: 14px;
    margin-bottom: 24px;
`;

const AISubscription = () => {
    const [activeTab, setActiveTab] = useState('plans');
    const [plans, setPlans] = useState([]);
    const [features, setFeatures] = useState([]);
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans`
            );
            if (response.success) {
                setPlans(response.data);
            }
        } catch (error) {
            message.error('Failed to fetch plans');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchFeatures = async () => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/feature-catalog`
            );
            if (response.success) {
                setFeatures(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch features:', error);
        }
    };

    const fetchPolicy = async () => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/policy`
            );
            if (response.success) {
                setPolicy(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch policy:', error);
        }
    };

    useEffect(() => {
        fetchPlans();
        fetchFeatures();
        fetchPolicy();
    }, []);

    const tabItems = [
        {
            key: 'plans',
            label: 'Plan Configuration',
            children: (
                <PlanConfiguration
                    plans={plans}
                    features={features}
                    policy={policy}
                    onRefresh={fetchPlans}
                    onPolicyRefresh={fetchPolicy}
                    loading={loading}
                />
            )
        },
        {
            key: 'requests',
            label: 'Subscription Requests',
            disabled: true,
            children: (
                <SubscriptionRequests
                    plans={plans}
                />
            )
        },
        {
            key: 'usage',
            label: 'Usage & Alerts',
            disabled: true,
            children: (
                <UsageAlerts />
            )
        },
        {
            key: 'user-tokens',
            label: 'User Token Management',
            disabled: true,
            children: (
                <UserTokenManagement />
            )
        }
    ];

    return (
        <PageContainer>
            <PageTitle>AI Subscription Plans</PageTitle>
            <PageDescription>
                Configure AI subscription plans, manage assignments, and monitor usage across organizations and customers.
            </PageDescription>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="large"
            />
        </PageContainer>
    );
};

export default AISubscription;
